<template>
  <header class="sticky top-0 z-30 bg-gray-50/95 backdrop-blur">
    <div class="nav-transition mx-auto max-w-10xl px-4 lg:px-8">
      <div class="grid items-start gap-4 py-4 md:grid-cols-[auto_1fr_auto]">
        <div class="flex items-center gap-2 self-start">
          <RouterLink to="/" class="text-lg font-semibold text-ink"><img src="@/assets/logo.png" alt="Invi8 Logo" style="height:40px"></RouterLink>  
        </div>

        <div class="flex flex-col items-center justify-center gap-2">
          <div v-if="showTabs" class="tabs-row">
            <button
              type="button"
              class="tab-link"
              :class="{ active: vendorsActive }"
              @click="setTab('vendors')"
            >
              <span class="tab-emoji"><img :src="tabIcons.vendors" alt="Vendors icon"></span>
              <span>Vendors</span>
            </button>
            <button
              type="button"
              class="tab-link"
              :class="{ active: venuesActive }"
              @click="setTab('venues')"
            >
              <span class="tab-emoji"><img :src="tabIcons.venues" alt="Venues icon"></span>
              <span>Venues</span>
            </button>
          </div>
          <SearchBox
            v-if="showSearch"
            :compact="navCollapsed"
            :context="activeTab"
            @expand="handleMiniExpand"
          />
        </div>

        <div class="flex items-center gap-3 justify-end self-start">
          <RouterLink class="hidden text-sm font-semibold text-ink md:inline" to="/vendor-onboard">Become a Vendor</RouterLink> 
          <div v-if="isLoggedIn" class="relative flex items-center">
            <button
              ref="profileButtonRef"
              type="button"
              class="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-2.5 py-1.5 text-sm font-semibold text-ink shadow-sm transition hover:border-gray-300"
              @click="profileMenuOpen = !profileMenuOpen"
            >
              <span class="flex h-8 w-8 items-center justify-center rounded-full bg-[#FFE5EC] text-brand">
                <i class="ri-user-3-line text-base"></i>
              </span>
              <span class="hidden lg:inline">{{ displayName }}</span>
              <i class="ri-arrow-down-s-line text-base text-muted"></i>
            </button>
            <transition name="fade">
              <div
                v-if="profileMenuOpen"
                ref="profileDropdownRef"
                class="absolute right-0 top-full mt-3 w-56 rounded-2xl border border-gray-100 bg-white p-3 shadow-[0_18px_40px_rgba(0,0,0,0.12)] z-40"
              >
                <div class="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-muted">Account</div>
                <RouterLink class="flex items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-ink transition hover:bg-gray-50" to="/dashboard/settings">
                  <span>Settings</span>
                  <i class="ri-settings-3-line text-sm text-muted"></i>
                </RouterLink>
                <button
                  type="button"
                  class="mt-1 flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-ink transition hover:bg-gray-50"
                  @click="handleLogout"
                >
                  <span>Logout</span>
                  <i class="ri-logout-box-r-line text-sm text-muted"></i>
                </button>
              </div>
            </transition>
          </div>
          <RouterLink
            v-else
            class="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-ink shadow-sm transition hover:border-gray-300"
            to="/login"
            aria-label="Log in"
          >
            <i class="ri-user-line text-lg"></i>
          </RouterLink>
          <button class="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-ink shadow-sm transition hover:border-gray-300 lg:hidden" aria-label="Open menu" @click="ui.toggleSidenav">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div class="relative hidden lg:block">
            <button ref="menuButtonRef" class="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-ink shadow-sm transition hover:border-gray-300" aria-label="Open menu" @click="desktopMenuOpen = !desktopMenuOpen">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <transition name="fade">
              <div
                v-if="desktopMenuOpen"
                ref="dropdownRef"
                class="absolute right-0 top-full mt-3 w-72 rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_18px_40px_rgba(0,0,0,0.12)] z-40"
              >
                <div class="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
                  <span class="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-lg">?</span>
                  <span>Navigate</span>
                </div>
                <div class="grid gap-2 text-sm text-ink">
                  <RouterLink
                    v-for="link in navLinks"
                    :key="link.to"
                    class="flex items-center justify-between rounded-xl px-3 py-2 transition hover:bg-gray-50"
                    :to="link.to"
                  >
                    <span class="font-semibold">{{ link.label }}</span>
                    <span class="text-xs text-muted">→</span>
                  </RouterLink>
                </div>
                <div class="mt-3 flex gap-2">
                  <RouterLink
                    v-if="!isLoggedIn"
                    class="flex-1 rounded-full border border-gray-200 px-3 py-2 text-center text-xs font-semibold text-ink transition hover:border-brand hover:text-brand"
                    to="/login"
                  >
                    Log in
                  </RouterLink>
                  <RouterLink
                    v-if="!isLoggedIn"
                    class="flex-1 rounded-full bg-brand px-3 py-2 text-center text-xs font-semibold text-white transition hover:bg-brandDark"
                    to="/register"
                  >
                    Register
                  </RouterLink>
                  <RouterLink
                    v-if="isLoggedIn"
                    class="flex-1 rounded-full border border-gray-200 px-3 py-2 text-center text-xs font-semibold text-ink transition hover:border-brand hover:text-brand"
                    to="/dashboard/settings"
                  >
                    Settings
                  </RouterLink>
                  <button
                    v-if="isLoggedIn"
                    type="button"
                    class="flex-1 rounded-full bg-brand px-3 py-2 text-center text-xs font-semibold text-white transition hover:bg-brandDark"
                    @click="handleLogout"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import SearchBox from '@/components/common/SearchBox.vue'
import { useUiStore } from '@/stores/ui'
import { clearAuth, getAuthUser, isAuthenticated } from '@/services/auth'

const ui = useUiStore()
const route = useRoute()
const router = useRouter()

const desktopMenuOpen = ref(false)
const dropdownRef = ref(null)
const menuButtonRef = ref(null)
const profileMenuOpen = ref(false)
const profileDropdownRef = ref(null)
const profileButtonRef = ref(null)
const activeTab = ref(route.name === 'venues' ? 'venues' : 'vendors')

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/vendors', label: 'Vendors' },
  { to: '/venues', label: 'Venues' }, 
  { to: '/pricing', label: 'Pricing' },
  { to: '/faqs', label: 'FAQs' },
  { to: '/categories', label: 'Categories' },
  { to: '/use-cases', label: 'Use Cases' },
  { to: '/blog', label: 'Blog' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
  { to: '/privacy-policy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' }
]
 
const setTab = (tab) => {
  activeTab.value = tab
}

const vendorsActive = computed(() => activeTab.value === 'vendors')
const venuesActive = computed(() => activeTab.value === 'venues')

const isVendorDetail = computed(() => (router.currentRoute.value.path || '').startsWith('/vendor/'))
const currentPath = computed(() => router.currentRoute.value.path || '')
const currentName = computed(() => router.currentRoute.value.name || '')

const hiddenPaths = ['/vendor-onboard', '/login', '/register', '/forgot-password', '/dashboard', '/404', '/not-found']
const hiddenNames = ['VendorOnboard', 'Login', 'Register', 'ForgotPassword', 'Dashboard', 'DashboardVenues', 'DashboardPackages', 'DashboardSettings', 'NotFound']

const hideNavUi = computed(() => {
  const path = currentPath.value.toLowerCase()
  if (isVendorDetail.value) return true
  if (hiddenPaths.some((p) => path.startsWith(p))) return true
  const name = (currentName.value || '').toString().toLowerCase()
  return hiddenNames.map((n) => n.toLowerCase()).includes(name)
})

const showSearch = computed(() => (vendorsActive.value || venuesActive.value) && !hideNavUi.value)
const navCollapsed = ref(false)
const forceExpanded = ref(false)
const forceExpandedAt = ref(0)
const showTabs = computed(() => (vendorsActive.value || venuesActive.value) && !navCollapsed.value && !hideNavUi.value)

const tabIcons = {
  vendors: new URL('@/assets/store.gif', import.meta.url).href,
  venues: new URL('@/assets/venues.gif', import.meta.url).href
}

const handleClickOutside = (event) => {
  const target = event.target
  if (desktopMenuOpen.value) {
    if (dropdownRef.value && dropdownRef.value.contains(target)) return
    if (menuButtonRef.value && menuButtonRef.value.contains(target)) return
    desktopMenuOpen.value = false
  }
  if (profileMenuOpen.value) {
    if (profileDropdownRef.value && profileDropdownRef.value.contains(target)) return
    if (profileButtonRef.value && profileButtonRef.value.contains(target)) return
    profileMenuOpen.value = false
  }
}

const handleEsc = (event) => {
  if (event.key === 'Escape') {
    desktopMenuOpen.value = false
    profileMenuOpen.value = false
  }
}

const collapseThreshold = 80
const handleScroll = () => {
  const y = window.scrollY || document.documentElement.scrollTop
  const nearTop = y < 12

  if (nearTop) {
    navCollapsed.value = false
    forceExpanded.value = false
    return
  }

  if (forceExpanded.value) {
    const moved = Math.abs(y - forceExpandedAt.value) > 50
    if (moved && y > forceExpandedAt.value) {
      forceExpanded.value = false
    } else {
      navCollapsed.value = false
      return
    }
  }

  navCollapsed.value = y > collapseThreshold
}

const handleMiniExpand = () => {
  forceExpanded.value = true
  forceExpandedAt.value = window.scrollY || document.documentElement.scrollTop
  navCollapsed.value = false
}

const isLoggedIn = ref(isAuthenticated())
const userProfile = ref(getAuthUser())

const refreshAuthState = () => {
  isLoggedIn.value = isAuthenticated()
  userProfile.value = getAuthUser()
}

const displayName = computed(() => {
  const user = userProfile.value || {}
  return (
    user.name ||
    user.full_name ||
    user.fullName ||
    user.username ||
    user.email ||
    'Account'
  )
})

const handleLogout = () => {
  clearAuth()
  refreshAuthState()
  router.push('/login')
}

watch(
  () => router.currentRoute.value.name || router.currentRoute.value.path,
  (val) => {
    const pathStr = val || ''
    if (pathStr.toString().includes('venue')) {
      activeTab.value = 'venues'
    } else {
      activeTab.value = 'vendors'
    }
    refreshAuthState()
  },
  { immediate: true }
)

const removeAfterEach = router.afterEach(() => {
  desktopMenuOpen.value = false
  profileMenuOpen.value = false
})

onMounted(() => {
  window.addEventListener('click', handleClickOutside)
  window.addEventListener('keydown', handleEsc)
  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll()
  refreshAuthState()
})

onUnmounted(() => {
  window.removeEventListener('click', handleClickOutside)
  window.removeEventListener('keydown', handleEsc)
  window.removeEventListener('scroll', handleScroll)
  if (typeof removeAfterEach === 'function') removeAfterEach()
})
</script>

<style scoped>


.nav-transition {
  transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes bounceSlow {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-2px); }
}

.animate-bounce-slow {
  animation: bounceSlow 1.2s infinite;
}

.nav-transition a.router-link-active {
  color: var(--accent, #ec2263);
}

.tabs-row {
  display: inline-flex;
  align-items: center;
  gap: 26px;
}

.tab-link {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px 10px;
  font-size: 14px;
  font-weight: 600;
  color: #666;
  border-bottom: 2px solid transparent;
}

.tab-link.active {
  color: #111;
  border-color: #111;
}

.tab-emoji {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
}

.tab-emoji img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  mix-blend-mode: multiply;
  background: transparent;
}

.pill-badge {
  padding: 2px 8px;
  border-radius: 12px;
  background: #1f2d3d;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
}
</style>
